import React from 'react';
import { ArrowRight, Sparkles } from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';
import useScrollAnimation from '../hooks/useScrollAnimation';
import useCountUp from '../hooks/useCountUp';
import './Hero.css';

const containerVariants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.18, delayChildren: 0.3 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 40, filter: 'blur(10px)' },
  visible: { opacity: 1, y: 0, filter: 'blur(0px)', transition: { duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] } },
};

const Hero = () => {
  const prefersReducedMotion = useReducedMotion();
  const { ref: statsRef, isVisible: statsVisible } = useScrollAnimation({ threshold: 0.3 });

  const yearsCount = useCountUp('10+', statsVisible);
  const projectsCount = useCountUp('50+', statsVisible);
  const clientsCount = useCountUp('20+', statsVisible);

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="hero" className="hero-section">
      {/* Animated mesh gradient background */}
      <div className="hero-mesh-gradient" />
      <div className="hero-grid-overlay" />
      <div className="hero-background-image" />
      
      {/* Floating orbs */}
      <div className="hero-orbs">
        <div className="orb orb-1" />
        <div className="orb orb-2" />
        <div className="orb orb-3" />
      </div>
      
      {/* Enhanced particles */}
      <div className="hero-particles">
        {[...Array(12)].map((_, i) => (
          <div key={i} className={`particle particle-${i + 1}`} />
        ))}
      </div>

      <div className="hero-container">
        <motion.div
          className="hero-content"
          variants={prefersReducedMotion ? {} : containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div className="hero-badge" variants={itemVariants}>
            <Sparkles size={14} className="hero-badge-icon" />
            <span>Innovation Meets Intelligence</span>
          </motion.div>

          <motion.h1 className="display-huge hero-title" variants={itemVariants}>
            <span className="gradient-text">AI-Powered</span> Technology
            <br />Solutions for Modern
            <br />Businesses
          </motion.h1>

          <motion.p className="body-large hero-description" variants={itemVariants}>
            Building cutting-edge communication, healthcare, and retail platforms that help businesses and families operate smarter and more efficiently.
          </motion.p>

          <motion.div className="hero-cta-group" variants={itemVariants}>
            <motion.button
              className="btn-primary hero-cta-btn"
              onClick={scrollToContact}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.97 }}
              transition={{ type: 'spring', stiffness: 400, damping: 17 }}
            >
              Get Started
              <ArrowRight size={20} />
            </motion.button>
          </motion.div>

          <motion.div
            ref={statsRef}
            className={`hero-stats ${statsVisible ? 'stats-visible' : ''}`}
            variants={itemVariants}
          >
            {[
              { value: yearsCount, label: 'Years Experience' },
              { value: projectsCount, label: 'Projects Delivered' },
              { value: clientsCount, label: 'Happy Clients' },
            ].map((stat, i) => (
              <React.Fragment key={i}>
                {i > 0 && <div className="stat-divider" />}
                <motion.div
                  className="stat-item"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={statsVisible ? { opacity: 1, scale: 1 } : {}}
                  transition={{ duration: 0.5, delay: i * 0.15 }}
                >
                  <div className="stat-number neon-glow">{stat.value}</div>
                  <div className="stat-label">{stat.label}</div>
                </motion.div>
              </React.Fragment>
            ))}
          </motion.div>
        </motion.div>
      </div>
      
      {/* Bottom gradient fade */}
      <div className="hero-bottom-fade" />
    </section>
  );
};

export default Hero;